import * as _ from 'lodash';

import { Attribute } from './attribute';
import { AttributeDefinitionValueListItem } from './attributeDefinitionValueListItem';
import { AttributeDefinition } from './attributeDefinition';
import { AttributeDefinitionCondition } from './attributeDefinitionCondition';
import { FlatComplexAttributeValue } from './flatAttribute';
import * as moment from 'moment';

export class EditableAttribute extends Attribute {
  public _min: string;
  public _max: string;
  private _isVisible: boolean;
  public conditions: AttributeDefinitionCondition[];
  public complexAttributeValues: FlatComplexAttributeValue[];
  public decryptedValue: string;

  public static readonly INVALID_DATE_VALUE: string = 'Invalid Date';

  constructor(attribute: Attribute, definition: AttributeDefinition) {
    super(
      attribute.id,
      attribute.name,
      definition.description,
      definition.adminDocumentation,
      attribute.value,
      attribute.type,
      definition.entityType,
      attribute.attributeDefinitionId,
      definition.required,
      definition.isReadOnly,
      definition.isHidden,
      attribute.featured,
      definition.allowsUserAddedValues,
      attribute.createdBy,
      attribute.createdOn,
      attribute.updatedBy,
      attribute.updatedOn
    );
    this.name = definition.name;
    this.description = definition.description;
    this.adminDocumentation = definition.adminDocumentation;
    this._min = definition.min;
    this._max = definition.max;
    this.valueList = [];
    this.attributeDefinitionValueListItem = attribute.attributeDefinitionValueListItem;
    this.conditions = definition.conditions;
    this._isVisible = true;
    this.subFieldList = definition.subFieldList;
  }

  public get isVisible(): boolean {
    return this._isVisible;
  }

  public hide() {
    this._isVisible = false;
  }

  public show() {
    this._isVisible = true;
  }

  public get hasCondition(): boolean {
    return this.conditions && this.conditions.length > 0 && this.hasAnyConditionValue;
  }
  public get allowsMultipleValues(): boolean {
    return super.isCheckboxType() || super.isMultiSelectType();
  }

  private get hasAnyConditionValue(): boolean {
    let result = this.conditions.findIndex((x) => x.conditionValues.length > 0) != -1;
    return result;
  }
  public get needsRangeCheck(): boolean {
    return super.isNumberType() && this.hasRange;
  }

  public get needsRangeCheckComplex(): boolean {
    return super.isComplexType() && this.hasRange;
  }

  public get needsDateRangeCheck(): boolean {
    return super.isDateRangeType();
  }

  public get needsDateCheck(): boolean {
    return super.isDateType();
  }

  public get isValid(): boolean {
    if (!this.isVisible) {
      return true;
    }

    if (this.required && !this.hasValue()) {
      return false;
    }

    if (this.needsRangeCheck && this.hasValue()) {
      return this.isInRange;
    }

    if (this.needsDateRangeCheck) {
      return this.isInDateRange;
    }

    if (this.needsDateCheck) {
      return this.isValidDate;
    }

    if (this.needsRangeCheckComplex) {
      return this.complexInRange;
    }

    return true;
  }

  public get profileisValid(): boolean {
    if (!this.isVisible) {
      return true;
    }

    if (this.required && !this.hasValue()) {
      return false;
    }

    if (this.needsRangeCheck && this.hasValue()) {
      return this.isInRange;
    }

    if (this.needsDateRangeCheck) {
      return this.isInDateRange;
    }

    if (this.needsDateCheck) {
      return this.isValidDate;
    }

    return true;
  }

  public setOrAddValue(value: string | AttributeDefinitionValueListItem) {
    if (super.isDropDownType()) {
      this.attributeDefinitionValueListItem = <AttributeDefinitionValueListItem>value;
      this.value = this.attributeDefinitionValueListItem?.itemValue;
    } else if (this.allowsMultipleValues) {
      let attributeDefinitionValueListItem = AttributeDefinitionValueListItem.parse(value);
      this.valueList.push(attributeDefinitionValueListItem);
    } else {
      this.value = <string>value;
    }
  }

  public removeValue(value: string) {
    _.remove(this.valueList, { itemValue: value });
  }

  public hasValue(): boolean {
    if (this.isComplexType()) {
      if (this.complexAttributeValues.length > 0) {
        let requiredSubFieldList = this.subFieldList
          .filter((x) => x.required == true)
          .map((y) => y.id);
        let complexAttributeItems = this.complexAttributeValues[0].items.map(
          (x) => x.subFieldDefinitionItemId
        );
        return requiredSubFieldList.every((item) => complexAttributeItems.includes(item));
      } else return false;
    }
    return this.allowsMultipleValues ? !_.isEmpty(this.valueList) : super.hasValue();
  }

  public get complexInRange(): boolean {
    let minInRange = this._min ? this.complexAttributeValues.length >= Number(this._min) : true;
    let maxIRange = this._max ? this.complexAttributeValues.length <= Number(this._max) : true;
    return minInRange && maxIRange;
  }

  private get hasRange(): boolean {
    const hasMin = !isNaN(Number(this._min));
    const hasMax = !isNaN(Number(this._max));
    return hasMin || hasMax;
  }

  private get isInRange(): boolean {
    const isValueParseable = (limit: string): boolean => !_.isNull(limit) && !_.isUndefined(limit);
    const hasMin = isValueParseable(this._min) && !isNaN(Number(this._min));
    const hasMax = isValueParseable(this._max) && !isNaN(Number(this._max));
    const hasValue = isValueParseable(this.value) && !isNaN(Number(this.value));

    if (!hasValue) {
      return !this.required;
    }

    let value = Number(this.value);
    let min = Number(this._min);
    let max = Number(this._max);

    if (hasMin && hasMax) {
      return value >= min && max >= value;
    } else if (hasMin) {
      return value >= min;
    } else if (hasMax) {
      return max >= value;
    } else {
      return true;
    }
  }

  private get isValidDate(): boolean {
    if (this.value === EditableAttribute.INVALID_DATE_VALUE) {
      return false;
    }
    return true;
  }

  private get isInDateRange(): boolean {
    let startDate;
    let endDate;
    let present: boolean = false;
    if (this.value) {
      let valueArray = this.value.replace(/ /g, '').split('|');
      if (valueArray.length > 0) {
        startDate = new Date(valueArray[0]);
        if (isNaN(startDate)) {
          return false;
        }
        if (valueArray.length == 2) {
          if (valueArray[1].localeCompare('Present', 'en', { sensitivity: 'base' }) == 0) {
            present = true;
          } else {
            endDate = new Date(valueArray[1]);
            if (isNaN(endDate)) {
              return false;
            }
          }
        }
        if (startDate && endDate && moment(startDate).isAfter(moment(endDate))) {
          return false;
        } else if (!startDate && (present || endDate)) {
          return false;
        } else if (startDate && !(present || endDate)) {
          return false;
        }
      }
    }
    return true;
  }

  public values(): string[] {
    let values: string[] = [];
    if (super.isDropDownType() || super.isSkillType()) {
      values.push(this.attributeDefinitionValueListItem?.id);
    } else if (this.allowsMultipleValues) {
      values = this.valueList.map((x) => x.id);
    } else {
      values.push(this.value);
    }
    return values;
  }

  static fromAttributeDefinition(attributeDefinition: AttributeDefinition): EditableAttribute {
    return new EditableAttribute(
      Attribute.parse({
        name: attributeDefinition.name,
        description: attributeDefinition.description,
        type: attributeDefinition.uiType,
        entityType: attributeDefinition.entityType,
        attributeDefinitionId: attributeDefinition.id,
        required: attributeDefinition.required,
        allowsUserAddedValues: attributeDefinition.allowsUserAddedValues,
        valueList: attributeDefinition.valueList,
      }),
      attributeDefinition
    );
  }
}
